import Stack from '@mui/material/Stack';

import {
  StyledActionButton,
  StyledActionButtonsContainer,
  StyledAvatarContainer,
  StyledDontShowContainer,
  StyledFooter,
  StyledHeader,
  StyledInfo,
  StyledProgressBarContainer,
  StyledStack,
} from '@webclient/components/notification/styles';

import useToggleState from '@webclient/hooks/useToggleState';

import CloseIcon from '~icons/knowz-iconify/cross';

import { Checkbox, IconButton } from '@knowz/knowz-ui';
import { Text } from '@knowz/knowz-ui';
import { ToastContentProps } from 'react-toastify/dist/types';

type NotificationProps = Partial<
  Pick<ToastContentProps['toastProps'], 'closeToast' | 'position'>
> & {
  type?: 'info' | 'success' | 'error' | 'warning';
  title?: React.ReactNode;
  message?: React.ReactNode;
  avatar?: React.ReactNode | string;
  isSquareAvatar?: boolean;
  showCloseButton?: boolean;
  showDontShowAgain?: boolean;
  actions?: {
    primary?: {
      label: string;
      onClick: () => void;
    };
    secondary?: {
      label: string;
      onClick: () => void;
    };
  };
  showProgressBar?: boolean;
  progress?: number;
  customContent?: React.ReactNode;
  id: string;
};

function Notification({
  closeToast,
  title,
  message,
  avatar,
  isSquareAvatar = false,
  showDontShowAgain = false,
  actions,
  showProgressBar = false,
  progress = 0,
  id,
}: NotificationProps) {
  const { state: showAgain, toggle: toggleShowAgain } = useToggleState(false);

  return (
    <StyledStack>
      <StyledHeader>
        <Stack sx={{ flexDirection: 'row', gap: 4 }}>
          {avatar && (
            <StyledAvatarContainer isSquare={isSquareAvatar}>
              {avatar}
            </StyledAvatarContainer>
          )}
          <StyledInfo>
            {title && (
              <Text
                variant="textSm"
                weight="semibold"
              >
                {title}
              </Text>
            )}
            <Text
              variant="textSm"
              weight="regular"
            >
              {message}
            </Text>
          </StyledInfo>
        </Stack>

        <IconButton
          variant="ghost"
          color="secondary"
          shape="fullRounded"
          size="small"
          onClick={closeToast}
          sx={{ padding: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </StyledHeader>

      {(showDontShowAgain || actions) && (
        <StyledFooter>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {showDontShowAgain && (
              <StyledDontShowContainer>
                <Checkbox
                  size="small"
                  onChange={toggleShowAgain}
                  checked={showAgain}
                />
                <Text
                  variant="textSm"
                  weight="medium"
                >
                  Don't show again
                </Text>
              </StyledDontShowContainer>
            )}
            {actions && (
              <StyledActionButtonsContainer>
                {actions.secondary && (
                  <StyledActionButton
                    disableRipple
                    buttonType="secondary"
                    onClick={actions.secondary.onClick}
                  >
                    {actions.secondary.label}
                  </StyledActionButton>
                )}
                {actions.primary && (
                  <StyledActionButton
                    disableRipple
                    buttonType="primary"
                    onClick={actions.primary.onClick}
                  >
                    {actions.primary.label}
                  </StyledActionButton>
                )}
              </StyledActionButtonsContainer>
            )}
          </Stack>
        </StyledFooter>
      )}

      {showProgressBar && <StyledProgressBarContainer progress={progress} />}
    </StyledStack>
  );
}

export { NotificationProps };
export default Notification;
